import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { makeStyles } from "tss-react/mui";

import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";

import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import { TAG_COLUMN_PREFIX } from "../data";

import KMKTable from "../../../../Common/Table/KMKTable";
import KMKNavigateButton from "../../../../Common/KMKNavigateButton";
import { navDataExplorer } from "../../../../kmk/AppData";
import useWindowResize from "../../../../../hooks/useWindowsReszie";
import { convertTimezone } from "../../../../../util/common";

const useStyles = makeStyles()({
  DataTableContainer: {
    borderRadius: 10,
    backgroundColor: "#FFFFFF",
    padding: "5px 0px 0px",
    overflowX: "auto",
    "& .MuiTableRow-head .MuiTableCell-root": {
      padding: "6px 16px",
      height: "37px",
    },
    "& .MuiTableRow-head .MuiTableCell-root:last-child": {
      paddingRight: "40px",
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      padding: "6px 16px",
      borderBottom: "none",
    },
    "& .MuiTableBody-root .MuiTableCell-root>.MuiIconButton-root:first-child": {
      marginLeft: "-10px",
      verticalAlign: "sub",
    },
    "& ::-webkit-scrollbar-track": {
      marginTop: "36px",
    },
  },
  IconHolder: {
    padding: 0,
    margin: "0 2px",
    fontSize: "22px",
    height: "32px",
    width: "32px",
    borderRadius: "64px",
  },
  flexBox: {
    display: "flex",
    alignContent: "center",
    alignItems: "center",
  },
});

function List({
  entities = [],
  selectedTags,
  dataLoaded,
  loading,
  handleEditClick,
  navigateToDashboard,
  handleDeleteClick,
}) {
  const { classes } = useStyles();
  const { Workspace_id } = useParams();

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("displayName");

  const { height: tableHeight } = useWindowResize(200);
  const workspace = useSelector((state) => state.workspace);
  const { accessRole, IsDataVaultEnabled } = workspace.workspace;

  const handleSorting = useCallback((property) => {
    setOrderBy((prevProperty) => {
      setOrder((prevOrder) => {
        const isAsc = prevProperty === property && prevOrder === "asc";
        return isAsc ? "desc" : "asc";
      });

      return property;
    });
  }, []);

  // If the sorting is done by any tag column and if that tag gets removed from selected tag then reseting sorting properties
  useEffect(() => {
    setOrderBy((prev) => {
      if (prev.startsWith(TAG_COLUMN_PREFIX) && !selectedTags.includes(prev.slice(TAG_COLUMN_PREFIX.length))) {
        setOrder("asc");
        return "displayName";
      } else return prev;
    });
  }, [selectedTags]);

  const handleRowClick = useCallback(
    (record) => {
      navigateToDashboard({ Object_id: record.id });
    },
    [navigateToDashboard]
  );

  const EntityColumns = useMemo(
    () =>
      !dataLoaded
        ? []
        : [
            {
              id: "displayName",
              name: "NAME",
              width: 150,
              align: "left",
              sortable: true,
              renderHeadCell: (name) => name,
              renderValueCell: (val, record, index) => val,
            },
            {
              id: "autoCount",
              name: "MATCH CONFIGURATION",
              width: 270,
              align: "left",
              sortable: false,
              renderHeadCell: (name) => name,
              renderValueCell: (val, record, index) => {
                const suspectCount = record.suspectCount || 0;
                return `Auto-Merge (${val}), Suspect Match (${suspectCount})`;
              },
            },
            {
              id: "fieldCount",
              name: "COLUMNS",
              width: 100,
              align: "left",
              sortable: true,
              renderHeadCell: (name) => name,
              renderValueCell: (val, record, index) => val || "-",
            },
            {
              id: "rowsCount",
              name: "ROWS",
              width: 100,
              align: "left",
              sortable: true,
              renderHeadCell: (name) => name,
              renderValueCell: (val, record, index) => (record?.isApplied && val ? val : "-"),
            },
            {
              id: "lastModified",
              name: "LAST UPDATED",
              width: 180,
              align: "left",
              sortable: true,
              renderHeadCell: (name) => name,
              renderValueCell: (val, record, index) => (val ? convertTimezone(val) : "-"),
            },
            ...selectedTags.map((tag) => ({
              id: `${TAG_COLUMN_PREFIX}${tag}`,
              name: tag,
              sortable: true,
              align: "left",
              width: 200,
              renderHeadCell: (name) => (
                <span className={classes.flexBox}>
                  {name}
                  {<LocalOfferOutlinedIcon sx={{ fontSize: "1.2rem", ml: "5px" }} />}
                </span>
              ),
              renderValueCell: (val, record, index) => val || "-",
              overflowTooltip: true,
            })),
            {
              id: "actions",
              name: "ACTIONS",
              width: 120,
              align: "center",
              sortable: false,
              renderHeadCell: (name) => name,
              renderValueCell: (val, record, index) => (
                <div style={{ display: "flex", justifyContent: "right", width: "75%", margin: "0 auto !important" }}>
                  <Box sx={{ minHeight: 35, minWidth: 35 }}>
                    <Tooltip title="Edit Configuration">
                      <IconButton
                        className={classes.IconHolder}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(record.id);
                        }}
                      >
                        <EditOutlinedIcon fontSize="inherit" color="primary" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {IsDataVaultEnabled !== 0 && (
                    <Box sx={{ minHeight: 35, minWidth: 35 }}>
                      {record.isApplied ? (
                        <KMKNavigateButton
                          iconButton
                          className={classes.IconHolder}
                          tooltipProps={{ title: "View in SQL Explorer" }}
                          navigationProps={{
                            to: {
                              pathname: `/my-workspaces/${Workspace_id}/${navDataExplorer.link}/`,
                              search: `sidebarView=${encodeURIComponent("DataSet")}&objectName=${encodeURIComponent(
                                record.name
                              )}&schema=${encodeURIComponent("MODEL")}&expandWS=true`,
                            },
                          }}
                        >
                          <TableChartOutlinedIcon color="secondary" />
                        </KMKNavigateButton>
                      ) : (
                        <></>
                      )}
                    </Box>
                  )}
                  {[1, 2].includes(accessRole) ? (
                    <Tooltip title="Delete">
                      <IconButton
                        className={classes.IconHolder}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick({ id: record.id, name: record.name });
                        }}
                      >
                        <DeleteOutlineOutlinedIcon fontSize="inherit" color="error" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <></>
                  )}
                </div>
              ),
            },
          ],
    [
      dataLoaded,
      selectedTags,
      classes.flexBox,
      classes.IconHolder,
      IsDataVaultEnabled,
      Workspace_id,
      accessRole,
      handleEditClick,
      handleDeleteClick,
    ]
  );

  // Sorting data as per the selection applied in the table header
  // Not doing sorting in the main file because sorting feature is specific to the List View
  const $sortedData = useMemo(() => {
    entities.sort((a, b) => {
      if (orderBy === "lastModified" || orderBy === "rowsCount" || orderBy === "fieldCount") {
        const aValue = orderBy === "lastModified" ? new Date(a[orderBy]) : parseInt(a[orderBy]);
        const bValue = orderBy === "lastModified" ? new Date(b[orderBy]) : parseInt(b[orderBy]);
        return order === "asc" ? aValue - bValue : bValue - aValue;
      } else return order === "asc" ? a[orderBy].localeCompare(b[orderBy]) : b[orderBy].localeCompare(a[orderBy]);
    });
    return [...entities];
  }, [order, orderBy, entities]);

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }} className={classes.DataTableContainer}>
      <KMKTable
        loading={loading}
        columns={EntityColumns}
        containerHeight={tableHeight}
        noDataText={"No records found"}
        data={$sortedData}
        sortingProperties={{ order, orderBy, handleSorting }}
        boldHeader
        tableProps={{ size: "medium", stickyHeader: true }}
        paginationProperties={{}}
        dataIdKey="id"
        onRowClick={handleRowClick}
      />
    </Paper>
  );
}

export default List;
